import { UserGeneralProfile, useRestApiProvider } from "@jugl-web/rest-api";
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { Subject, filter, map } from "rxjs";
import {
  UpdateUserProfileNamePayload,
  UpdateUserProfilePicturePayload,
  UserProfileProviderContext,
  UserProfileProviderContextValue,
} from "./UserProfileProviderContext";
import { RequestPayload, ResponsePayload, UserId } from "./types";
import { useUserProfileCache } from "./useUserProfileCache";

interface UserProfileProviderProps {
  children: ReactNode;
}

const BATCH_FLUSH_DELAY = 50;
const BATCH_SIZE = 20;

export const UserProfileProvider: FC<UserProfileProviderProps> = ({
  children,
}) => {
  const { usersApi } = useRestApiProvider();

  const [getUserGeneralProfile] = usersApi.useLazyGetUserGeneralProfileQuery();
  const [updateUserProfileMutation] = usersApi.useUpdateUserProfileMutation();
  const [updateUserProfilePictureMutation] =
    usersApi.useUpdateUserProfilePictureMutation();

  const { getCachedUserProfile, setCachedUserProfile } = useUserProfileCache();

  const userProfileUpdate$ = useMemo(
    () =>
      new Subject<{
        entityId: string;
        userId: string;
        profile: UserGeneralProfile;
      }>(),
    []
  );

  const pendingRequestsRef = useRef(
    new Map<
      UserId,
      {
        promise: Promise<ResponsePayload>;
        resolve: (value: ResponsePayload) => void;
        reject: (reason?: unknown) => void;
      }
    >()
  );

  const batchQueueRef = useRef<RequestPayload[]>([]);
  const batchEntityIdRef = useRef<string | null>(null);
  const timeoutIdRef = useRef<number | null>(null);

  const resetFlushTimeout = useCallback(() => {
    if (timeoutIdRef.current) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const flushBatch = useCallback(async () => {
    const batchItems = [...batchQueueRef.current];

    if (batchItems.length === 0) {
      return;
    }

    batchQueueRef.current = [];
    batchEntityIdRef.current = null;
    resetFlushTimeout();

    // We should always have the same entityId for all requests in the batch
    const { entityId } = batchItems[0];

    // Extract all unique userIds from the batch
    const userIds = [...new Set(batchItems.map((request) => request.userId))];

    const fetchedUserIds = new Set<string>();

    try {
      const profiles = await getUserGeneralProfile({
        entityId,
        userIds,
      }).unwrap();

      profiles.forEach((profile) => {
        // Add fetched profile to the cache
        setCachedUserProfile(entityId, profile.id, profile);
        userProfileUpdate$.next({ entityId, userId: profile.id, profile });
        fetchedUserIds.add(profile.id);

        const pendingRequest = pendingRequestsRef.current.get(profile.id);

        // Resolve pending promise for this user
        if (pendingRequest) {
          pendingRequest.resolve({
            entityId,
            userId: profile.id,
            profile,
          });
        }

        pendingRequestsRef.current.delete(profile.id);
      });

      // Reject all pending promises for users that were not found in the batch
      userIds.forEach((userId) => {
        if (!fetchedUserIds.has(userId)) {
          const pendingRequest = pendingRequestsRef.current.get(userId);

          if (pendingRequest) {
            pendingRequest.reject(new Error("User profile not found"));
          }

          pendingRequestsRef.current.delete(userId);
        }
      });
    } catch (error) {
      // When the batch request fails, reject all pending promises
      userIds.forEach((userId) => {
        const pendingRequest = pendingRequestsRef.current.get(userId);

        if (pendingRequest) {
          pendingRequest.reject(error);
        }

        pendingRequestsRef.current.delete(userId);
      });
    }
  }, [
    getUserGeneralProfile,
    resetFlushTimeout,
    setCachedUserProfile,
    userProfileUpdate$,
  ]);

  const enqueueToBatch = useCallback(
    async (payload: RequestPayload): Promise<ResponsePayload> => {
      // Existing batch should be flushed immediately if:
      const shouldFlushImmediately =
        // 1. the batch is full
        batchQueueRef.current.length >= BATCH_SIZE ||
        // 2. different entityId was detected in the middle of the batch
        (batchEntityIdRef.current &&
          batchEntityIdRef.current !== payload.entityId);

      if (shouldFlushImmediately) {
        flushBatch();
      }

      // If the batch is empty, set the entityId for this batch
      if (!batchEntityIdRef.current) {
        batchEntityIdRef.current = payload.entityId;
      }

      batchQueueRef.current.push(payload);

      // Debounce to accumulate requests in the batch
      resetFlushTimeout();
      timeoutIdRef.current = window.setTimeout(flushBatch, BATCH_FLUSH_DELAY);

      let resolveFn!: (value: ResponsePayload) => void;
      let rejectFn!: (reason?: unknown) => void;

      // Create a pending promise and their resolvers for further resolution
      const promise = new Promise<ResponsePayload>((resolve, reject) => {
        resolveFn = resolve;
        rejectFn = reject;
      });

      pendingRequestsRef.current.set(payload.userId, {
        promise,
        resolve: resolveFn,
        reject: rejectFn,
      });

      return promise;
    },
    [flushBatch, resetFlushTimeout]
  );

  const requestUserProfile = useCallback(
    async (payload: RequestPayload): Promise<ResponsePayload> => {
      // 1. If the profile is cached, return it immediately
      const cachedProfile = getCachedUserProfile(
        payload.entityId,
        payload.userId
      );

      if (cachedProfile) {
        return {
          entityId: payload.entityId,
          userId: payload.userId,
          profile: cachedProfile,
        };
      }

      // 2. If the profile has been already requested, return the pending promise
      const pendingRequest = pendingRequestsRef.current.get(payload.userId);

      if (pendingRequest) {
        return pendingRequest.promise;
      }

      // 3. New request -> enqueue to batch
      return enqueueToBatch(payload);
    },
    [enqueueToBatch, getCachedUserProfile]
  );

  const refetchUserProfile = useCallback(
    async (entityId: string, userId: string) => {
      try {
        const profiles = await getUserGeneralProfile({
          entityId,
          userIds: [userId],
        }).unwrap();

        if (profiles.length !== 1) {
          throw new Error("Invalid update profile response");
        }

        const [profile] = profiles;

        setCachedUserProfile(entityId, profile.id, profile);

        userProfileUpdate$.next({
          entityId,
          userId,
          profile,
        });
      } catch {
        // Just swallow the error
      }
    },
    [getUserGeneralProfile, setCachedUserProfile, userProfileUpdate$]
  );

  const updateUserProfileName = useCallback(
    async ({
      entityId,
      generalProfileId,
      userId,
      firstName,
      lastName,
    }: UpdateUserProfileNamePayload) => {
      const result = await updateUserProfileMutation({
        user_id: generalProfileId,
        data: {
          type: "general",
          first_name: firstName,
          last_name: lastName,
        },
      }).unwrap();

      refetchUserProfile(entityId, userId);

      return result;
    },
    [refetchUserProfile, updateUserProfileMutation]
  );

  const updateUserProfilePicture = useCallback(
    async ({ entityId, userId, file }: UpdateUserProfilePicturePayload) => {
      const result = await updateUserProfilePictureMutation(
        file ?? undefined
      ).unwrap();

      refetchUserProfile(entityId, userId);

      return result;
    },
    [refetchUserProfile, updateUserProfilePictureMutation]
  );

  const watchProfileUpdates = useCallback(
    (
      payload: { entityId: string; userId: string },
      callback: (profile: UserGeneralProfile) => void
    ) => {
      return userProfileUpdate$
        .pipe(
          filter(
            (p) =>
              p.entityId === payload.entityId && p.userId === payload.userId
          ),
          map((p) => p.profile)
        )
        .subscribe(callback);
    },
    [userProfileUpdate$]
  );

  useEffect(() => {
    return () => {
      resetFlushTimeout();
    };
  }, [resetFlushTimeout]);

  const contextValue = useMemo<UserProfileProviderContextValue>(
    () => ({
      requestUserProfile,
      getCachedUserProfile,
      watchProfileUpdates,
      updateUserProfileName,
      updateUserProfilePicture,
    }),
    [
      getCachedUserProfile,
      requestUserProfile,
      watchProfileUpdates,
      updateUserProfileName,
      updateUserProfilePicture,
    ]
  );

  return (
    <UserProfileProviderContext.Provider value={contextValue}>
      {children}
    </UserProfileProviderContext.Provider>
  );
};
